// AI-assisted project description generator.
// Calls the Anthropic Claude API directly from the browser. This is a
// prototype-mode integration — the API key ships in the frontend bundle, so
// only enable this in environments where that's acceptable (e.g. internal
// staging). Production should proxy the call through a backend endpoint.

const API_URL = "https://api.anthropic.com/v1/messages";
const MODEL = "claude-sonnet-4-6";
const ANTHROPIC_VERSION = "2023-06-01";

// Webpack's DefinePlugin replaces this token at build time with the literal
// string value (or `undefined` if the env var wasn't set). We read it the same
// way the rest of the codebase does; the bundle is safe because the entry is
// listed in DefinePlugin.
const getApiKey = (): string | undefined => {
  const k = process.env.REACT_APP_ANTHROPIC_API_KEY;
  return typeof k === "string" && k.length > 0 ? k : undefined;
};

export const isAiConfigured = (): boolean => getApiKey() !== undefined;

export interface ProjectContext {
  projectName?: string;
  builderName?: string;
  projectType?: string;
  projectDelivery?: string;
  city?: string;
  state?: string;
  landmark?: string;
  addressLine1?: string;
  projectSizeInAcres?: string;
  projectSaleableAreaSqft?: string;
  amenityNames?: string[];
}

export interface ProjectDescription {
  headline: string;
  lede: string;
  body: string;
  quote: string;
}

const SYSTEM_PROMPT = `You are a real-estate marketing copywriter for OpenSky, an immersive property experience platform for Indian real-estate developers. You write concise, evocative project descriptions that read like premium developer collateral — never generic, never salesy.

You will receive structured project context. Output a JSON object with exactly four string fields:
- headline: One strong hook line (8-15 words). The defining sentence of the project.
- lede: An opening summary of 2-3 sentences. Lead with location and scale, then signature feature. ~40-60 words.
- body: 2-3 paragraphs, each separated by a blank line. Describe design philosophy, lifestyle, sustainability, and what makes the project distinct. ~150-250 words total.
- quote: A short pull-quote attributed to a fictional but plausible architect or developer voice. 1-2 sentences, end with attribution like "— Lead Architect" or "— Project Director". ~25-40 words.

Constraints:
- Indian English, no hyperbole, no marketing clichés ("an oasis", "a sanctuary", "luxury redefined").
- Don't invent specific numbers or amenities that aren't in the context unless clearly implied.
- Output ONLY the JSON object, no preamble, no explanation, no markdown fencing.`;

const contextBlock = (ctx: ProjectContext): string => {
  const lines: string[] = [];
  const push = (label: string, v?: string | string[]) => {
    if (Array.isArray(v)) {
      if (v.length > 0) lines.push(`- ${label}: ${v.join(", ")}`);
    } else if (v && v.toString().trim().length > 0) {
      lines.push(`- ${label}: ${v}`);
    }
  };
  push("Project name", ctx.projectName);
  push("Builder", ctx.builderName);
  push("Project type", ctx.projectType);
  push("Delivery status", ctx.projectDelivery);
  push(
    "Location",
    [ctx.addressLine1, ctx.landmark, ctx.city, ctx.state].filter(Boolean).join(", "),
  );
  push("Site area (acres)", ctx.projectSizeInAcres);
  push("Saleable area (sqft)", ctx.projectSaleableAreaSqft);
  push("Amenities", ctx.amenityNames);
  return lines.join("\n");
};

const hasAnyContent = (d?: ProjectDescription): boolean =>
  !!d &&
  ((d.headline || "").trim().length > 0 ||
    (d.lede || "").trim().length > 0 ||
    (d.body || "").trim().length > 0 ||
    (d.quote || "").trim().length > 0);

const buildUserMessage = (
  ctx: ProjectContext,
  existing?: ProjectDescription,
  userHint?: string,
): string => {
  const ctxStr = contextBlock(ctx) || "(no structured context provided)";
  const hintStr = userHint && userHint.trim().length > 0
    ? `\n\nAdditional emphasis from the user:\n${userHint.trim()}`
    : "";

  if (hasAnyContent(existing)) {
    return [
      "Reframe and improve this existing project description. Keep the factual claims, sharpen the language, and ensure the four pieces (headline / lede / body / quote) read as a coherent whole.",
      "",
      "Project context:",
      ctxStr,
      "",
      "Existing description:",
      `- Headline: ${existing?.headline || "(empty)"}`,
      `- Lede: ${existing?.lede || "(empty)"}`,
      `- Body: ${existing?.body || "(empty)"}`,
      `- Quote: ${existing?.quote || "(empty)"}`,
      hintStr,
      "",
      "Return only the JSON object with the rewritten headline, lede, body, quote.",
    ].join("\n");
  }

  return [
    "Write a new project description from this context:",
    "",
    ctxStr,
    hintStr,
    "",
    "Return only the JSON object with headline, lede, body, quote.",
  ].join("\n");
};

const extractJson = (text: string): unknown => {
  // Try direct parse first
  try {
    return JSON.parse(text);
  } catch {
    // fall through
  }
  // Strip ```json fences if present
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenced) {
    try {
      return JSON.parse(fenced[1]);
    } catch {
      // fall through
    }
  }
  // Grab the first { ... } block
  const braceMatch = text.match(/\{[\s\S]*\}/);
  if (braceMatch) {
    return JSON.parse(braceMatch[0]);
  }
  throw new Error("AI response did not contain valid JSON.");
};

export async function generateProjectDescription(
  ctx: ProjectContext,
  existing?: ProjectDescription,
  userHint?: string,
): Promise<ProjectDescription> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error(
      "AI is not configured. Set REACT_APP_ANTHROPIC_API_KEY at build time.",
    );
  }

  const response = await fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": ANTHROPIC_VERSION,
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 2048,
      system: SYSTEM_PROMPT,
      messages: [
        {
          role: "user",
          content: buildUserMessage(ctx, existing, userHint),
        },
      ],
    }),
  });

  if (!response.ok) {
    let detail = "";
    try {
      detail = JSON.stringify(await response.json());
    } catch {
      detail = await response.text().catch(() => "");
    }
    throw new Error(
      `Claude request failed: ${response.status} ${response.statusText}${detail ? " — " + detail : ""}`,
    );
  }

  const data = await response.json();
  const text: string =
    Array.isArray(data?.content) && data.content[0]?.text ? data.content[0].text : "";
  if (!text) throw new Error("AI response was empty.");

  const parsed = extractJson(text) as Partial<ProjectDescription>;
  return {
    headline: String(parsed.headline ?? "").trim(),
    lede: String(parsed.lede ?? "").trim(),
    body: String(parsed.body ?? "").trim(),
    quote: String(parsed.quote ?? "").trim(),
  };
}

export const isReframeMode = (d?: ProjectDescription): boolean => hasAnyContent(d);
